import React, { useMemo } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { IconMessage2, IconPlugConnected, IconDeviceGamepad, IconArrowRight } from "@tabler/icons-react";
import {
  IonList,
  IonLabel,
  IonItem,
  IonHeader,
  isPlatform,
  IonContent,
  IonPage,
  IonSpinner,
  useIonToast,
  IonButtons,
  IonToolbar,
  IonBackButton,
} from "@ionic/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";

import {
  UrlWithStatus,
  useCrawlingActions,
  useCrawlingData,
} from "../../store/crawling";
import { useSockets } from "../../providers/socket";
import normalizeUrl from "./website/utils";

import AiApi from "../../api/ai";
import SkeletonText from "../SkeletonText";

import styles from "./styles.module.css";
import { useAppStore } from "../../store/app";
import SourcesAlert from "./sources/alert";

// Constants
import HEADER_DESCRIPTIONS, { NAV_ITEMS, MOBILE_HEADER_TITLES, EXPERIENCE_ITEMS } from "./menu";

import Onboarding from "./onboarding";

const isMobile = isPlatform("android") || isPlatform("ios");

type AiLayoutProps = {
  children: React.ReactNode;
};

const LayoutSkeleton = () => (
  <IonPage id="main-content">
    <IonContent>
      <div className="page-wrapper">
        <div className="w-100 h-100">
          <div className="row h-100">
            <div className="col-md-4">
              <div className="flex flex-column w-100 h-100 p-5">
                <SkeletonText rows={16} />
              </div>
            </div>
            <div className="col-md-8">
              <div className="flex flex-column w-100 h-100 p-5">
                <SkeletonText rows={16} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </IonContent>
  </IonPage>
);

export default function AiLayout({ children }: AiLayoutProps) {
  const location = useLocation();
  const history = useHistory();
  const path = location.pathname;

  const queryClient = useQueryClient();
  const intl = useIntl();
  const [present] = useIonToast();

  const { fullCrawl, singleCrawl } = useCrawlingData();
  const {
    setFullCrawl,
    setSingleCrawl,
    reset: resetCrawling,
  } = useCrawlingActions();
  const { isScanning } = fullCrawl;
  const { userSocket } = useSockets();
  const company = useAppStore((state) => state.user.company);
  const isOnboarded = useAppStore((state) => state.settings.onboarded);

  const headerContent = useMemo(
    () => HEADER_DESCRIPTIONS[path] || null,
    [path]
  );

  const mobileHeaderTitle = useMemo(() => {
    return MOBILE_HEADER_TITLES[path] || null;
  }, [path]);

  const isHome = path === "/ai";
  const isPlayground = path === "/ai/playground";
  const isPlaygroundWelcome = path.startsWith("/ai/playground/welcome");
  const isPlaygroundRoute = isPlayground || isPlaygroundWelcome;

  const { data, isLoading: isRequesting } = useQuery({
    queryFn: () => AiApi.getAiStatus(),
    queryKey: ["getAiStatus"],
    refetchOnWindowFocus: false,
    staleTime: 300000,
  });

  const setUrlUpdate = (message: any) => {
    const isSingleCrawl = message.source === "single-crawl";
    const currentArr = isSingleCrawl ? singleCrawl : fullCrawl?.urls || [];

    const indexToUpdate = currentArr.findIndex(
      (u) => normalizeUrl(u.url) === normalizeUrl(message.url)
    );
    if (indexToUpdate === -1) {
      const updatedArr = [
        ...currentArr,
        { ...message, active: message.completed },
      ];
      return isSingleCrawl
        ? setSingleCrawl(updatedArr)
        : setFullCrawl({ urls: updatedArr });
    }

    const updatedArr = [...currentArr];
    updatedArr[indexToUpdate] = {
      ...updatedArr[indexToUpdate],
      ...message,
      active: message.completed,
    };

    if (message.completed) {
      present({
        message: intl.formatMessage({ id: "aiUpdateSucces" }),
        duration: 4000,
        color: "light",
        position: "top",
        cssClass: "success-toast",
      });
    }

    return isSingleCrawl
      ? setSingleCrawl(updatedArr)
      : setFullCrawl({ urls: updatedArr });
  };

  React.useEffect(() => {
    if (!userSocket) return;
    const handleCompanyMessage = (event: any) => {
      // Only listen to events from current company;
      if (company?.id !== event?.companyId) return;

      if (
        event.data?.task !== "ai-crawling" &&
        event.data?.task !== "ai-single-crawling"
      )
        return;

      const handleCompletion = async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["getAiStatus"] }),
          queryClient.invalidateQueries({ queryKey: ["getWebsite"] }),
        ]);
      };

      if (event.data?.task === "ai-crawling") {
        const {
          message: {
            urlsWithStatus = [],
            isScanning = false,
            isOptimizing = false,
            failed = false,
            completed = false,
          } = {},
        } = event.data;

        if (!urlsWithStatus) return;

        if (failed) {
          present({
            message: intl.formatMessage({ id: "aiCrawlingFailed" }),
            duration: 5800,
            color: "light",
            position: "top",
            cssClass: "error-toast",
          });
          return resetCrawling();
        }

        if (completed) {
          handleCompletion();
        }

        setFullCrawl({
          urls: urlsWithStatus,
          isOptimizing,
          isScanning,
          failed,
        });
      }

      if (event.data?.task === "ai-single-crawling") {
        const message: UrlWithStatus = event.data?.message;
        if (!message) return;

        if (
          message.completed &&
          fullCrawl?.urls?.length === 0 &&
          singleCrawl?.length === 1
        ) {
          handleCompletion();
        }

        if (message.failed) {
          present({
            message: intl.formatMessage({ id: "aiCrawlingFailed" }),
            duration: 5800,
            color: "light",
            position: "top",
            cssClass: "error-toast",
          });
          resetCrawling();
          return handleCompletion();
        }

        setUrlUpdate(message);
      }
    };

    userSocket.on("company-message", handleCompanyMessage);
    return () => {
      userSocket.off("company-message");
    };
  }, [userSocket, fullCrawl, singleCrawl]);

  if (isRequesting) {
    return <LayoutSkeleton />;
  }

  const aiData: Record<string, any> | null = data?.status || null;
  const isTrained = aiData?.trained || false;

  return (
    <IonPage id="main-content">
      {isMobile && isOnboarded && !isPlaygroundWelcome && (
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/ai" />
            </IonButtons>

            {mobileHeaderTitle}
          </IonToolbar>
        </IonHeader>
      )}
      <IonContent
        {...(isMobile
          ? { fullscreen: true, color: "light", className: "ion-padding" }
          : {})}
      >
        <div
          className={
            isMobile
              ? `view-wrapper ${isPlaygroundRoute ? "h-100" : ""}`
              : "page-wrapper"
          }
        >
          {isMobile ? (
            <>
              {!isPlaygroundWelcome && headerContent && (
                <div className="flex flex-column gap-1">{headerContent}</div>
              )}
              {children}
            </>
          ) : isPlaygroundWelcome && isOnboarded ? (
            <div className="w-100 h-100">{children}</div>
          ) : (
            <div className="w-100 h-100 flex">
              {!isOnboarded && (isPlayground || isHome) ? (
                <Onboarding />
              ) : (
                <div className={styles.navInner}>
                  <div 
                    className={styles.statusCard} 
                    onClick={() => history.push("/ai")}
                    role="button"
                    tabIndex={0}
                  >
                    <div className={styles.statusHeader}>
                      <div className={styles.statusIndicator}>
                        <span className={`${styles.pulsingDot} ${!isTrained ? styles.warningDot : ""}`}></span>
                      </div>
                      <span className={styles.statusTitle}>
                        <FormattedMessage id={!isTrained ? "ai.sidebar.agent.setup" : "ai.sidebar.agent.active"} />
                      </span>
                    </div>
                    <p className={styles.statusText}>
                      <FormattedMessage
                        id={!isTrained ? "ai.sidebar.agent.setup.desc" : "ai.sidebar.agent.active.desc"}
                      />
                    </p>
                    
                    <div className={styles.cardArrow}>
                      <IconArrowRight size={16} />
                    </div>
                  </div>

                  <Link to="/ai/playground" className={styles.playgroundButton}>
                    <IconDeviceGamepad size={20} />
                    <span>Playground</span>
                    <IconArrowRight size={16} className="ml-auto" />
                  </Link>

                  <div className={styles.scrollArea}>
                    <div className={styles.navSection}>
                      <span className={styles.sectionLabel}>
                        <FormattedMessage id="ai.sidebar.sources" />
                      </span>
                      <div className={styles.navGrid}>
                        {NAV_ITEMS.filter((n) => n.path !== "/ai/providers").map(
                          ({ path: linkPath, label, key, icon }) => (
                            <IonItem
                              button
                              routerLink={linkPath}
                              key={key}
                              lines="none"
                              detail={false}
                              className={
                                location.pathname.includes(linkPath) 
                                  ? `${styles.navItem} ${styles.active}` 
                                  : styles.navItem
                              }
                            >
                              <div className={styles.iconWrapper}>{icon}</div>
                              <IonLabel className={styles.itemLabel}>
                                <FormattedMessage id={label} />
                                {key === "website" && isScanning && (
                                  <IonSpinner
                                    name="circular"
                                    style={{
                                      width: "14px",
                                      height: "14px",
                                      marginLeft: "6px",
                                      color: "var(--visito-color-1)"
                                    }}
                                  />
                                )}
                              </IonLabel>
                              {location.pathname.includes(linkPath) && <div className={styles.activeDot} />}
                            </IonItem>
                          )
                        )}
                      </div>
                    </div>

                    <div className={styles.navSection}>
                      <span className={styles.sectionLabel}>
                        <FormattedMessage id="ai.sidebar.behavior" />
                      </span>
                      <div className={styles.navGrid}>
                        {EXPERIENCE_ITEMS.map(({ path: linkPath, label, key, icon }) => (
                          <IonItem
                            button
                            routerLink={linkPath}
                            key={key}
                            lines="none"
                            detail={false}
                            className={
                              location.pathname.includes(linkPath)
                                ? `${styles.navItem} ${styles.active}`
                                : styles.navItem
                            }
                          >
                            <div className={styles.iconWrapper}>{icon}</div>
                            <IonLabel className={styles.itemLabel}>
                              <FormattedMessage id={label} />
                            </IonLabel>
                            {location.pathname.includes(linkPath) && <div className={styles.activeDot} />}
                          </IonItem>
                        ))}
                      </div>
                    </div>

                    <div className={styles.navSection}>
                      <span className={styles.sectionLabel}>
                        <FormattedMessage id="ai.sidebar.connections" />
                      </span>
                      <div className={styles.navGrid}>
                        <IonItem
                          button
                          routerLink="/ai/channels"
                          lines="none"
                          detail={false}
                          className={
                            location.pathname.includes("/channels")
                              ? `${styles.navItem} ${styles.active}`
                              : styles.navItem
                          }
                        >
                          <div className={styles.iconWrapper}>
                            <IconMessage2 size={18} />
                          </div>
                          <IonLabel className={styles.itemLabel}>
                            <FormattedMessage id="nav.channels" />
                          </IonLabel>
                          {(location.pathname.includes("/channels") || location.pathname.includes("/connect")) && <div className={styles.activeDot} />}
                        </IonItem>

                        <IonItem
                          button
                          routerLink="/ai/integrations"
                          lines="none"
                          detail={false}
                          className={
                            location.pathname.includes("/integrations")
                              ? `${styles.navItem} ${styles.active}`
                              : styles.navItem
                          }
                        >
                          <div className={styles.iconWrapper}>
                            <IconPlugConnected size={18} />
                          </div>
                          <IonLabel className={styles.itemLabel}>
                            <FormattedMessage id="nav.integrations" />
                          </IonLabel>
                          {location.pathname.includes("/integrations") && <div className={styles.activeDot} />}
                        </IonItem>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className={!isOnboarded ? styles.mainContent : styles.centerContent}>
                {isOnboarded && !isTrained && (isPlayground || isHome) ? (
                  <SourcesAlert />
                ) : (
                  <div
                    className={`w-full flex flex-column h-100 default-bg`}
                  >
                    {headerContent && isOnboarded ? headerContent : null}
                    {children}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
